import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { useActiveCompanyId } from '@/lib/active-company';
import { useSnapshot, useSnapshots } from '@/lib/snapshots';
import { cn } from '@/lib/cn';
import { compareSnapshots, type ModuleDiffStatus } from './_shared/snapshot-diff';

export function SnapshotCompare() {
  const companyId = useActiveCompanyId();
  const [params, setParams] = useSearchParams();
  const fromId = params.get('from') ?? undefined;
  const toId = params.get('to') ?? undefined;

  const snapshots = useSnapshots(companyId);
  const fromSnap = useSnapshot(companyId, fromId);
  const toSnap = useSnapshot(companyId, toId);

  if (!companyId) return <p className="p-10">Select a company first.</p>;
  if (!fromId || !toId) {
    return (
      <Empty
        title="Pick two snapshots to compare"
        description="Open a snapshot, then choose Compare to previous, or pick two from the archive."
      />
    );
  }
  if (fromSnap.isLoading || toSnap.isLoading)
    return <p className="p-10 text-text-secondary">Loading…</p>;
  if (!fromSnap.data || !toSnap.data) {
    return <Empty title="Snapshot not found" />;
  }

  const diff = compareSnapshots(fromSnap.data.payload, toSnap.data.payload);
  const fromLabel = fromSnap.data.name ?? new Date(fromSnap.data.generatedAt).toLocaleString();
  const toLabel = toSnap.data.name ?? new Date(toSnap.data.generatedAt).toLocaleString();

  return (
    <div className="mx-auto flex max-w-content-wide flex-col gap-gap-group px-5 py-gap-group md:px-10 lg:px-20">
      <header className="flex flex-wrap items-end justify-between gap-6 border-b border-border pb-gap-element-lg">
        <div>
          <p className="text-label uppercase tracking-[0.08em] text-accent">
            Snapshot comparison
          </p>
          <h1 className="mt-2 font-display text-h1 font-bold tracking-tight">
            What changed
          </h1>
          <p className="mt-2 text-body text-text-secondary">
            Comparing <strong>{fromLabel}</strong> → <strong>{toLabel}</strong>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/report/snapshots"
            className="text-body-sm font-medium text-text-secondary hover:text-accent"
          >
            ← Back to snapshots
          </Link>
        </div>
      </header>

      <SnapshotPicker
        snapshots={snapshots.data ?? []}
        fromId={fromId}
        toId={toId}
        onChange={(next) => setParams(next, { replace: true })}
      />

      <section className="grid gap-gap-element-lg md:grid-cols-3">
        <Tile
          label="Compliance"
          fromValue={`${diff.totals.fromPct}%`}
          toValue={`${diff.totals.toPct}%`}
          delta={diff.totals.pctDelta}
          unit="pts"
          improved={diff.totals.pctDelta >= 0}
        />
        <Tile
          label="Items in scope"
          fromValue={String(diff.totals.fromTotal)}
          toValue={String(diff.totals.toTotal)}
          delta={diff.totals.toTotal - diff.totals.fromTotal}
          unit=""
          improved={diff.totals.toTotal >= diff.totals.fromTotal}
          neutral
        />
        <Tile
          label="Open warnings"
          fromValue={String(diff.totals.fromWarnings)}
          toValue={String(diff.totals.toWarnings)}
          delta={diff.totals.warningsDelta}
          unit=""
          improved={diff.totals.warningsDelta <= 0}
        />
      </section>

      <section
        className="rounded-lg border border-border bg-surface"
        data-testid="module-diff"
      >
        <div className="border-b border-border p-7">
          <h2 className="font-display text-h3 font-semibold">Modules</h2>
          <p className="mt-1 text-body-sm text-text-secondary">
            Largest changes first. Regressions appear above improvements at ties.
          </p>
        </div>
        <ul className="divide-y divide-border">
          {diff.modules.map((m) => (
            <li
              key={m.id}
              className="flex items-center justify-between gap-4 px-7 py-4"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-text-primary">
                  {m.name}{' '}
                  <span className="text-text-secondary">({m.code})</span>
                </p>
                <p className="text-body-sm text-text-secondary">
                  {m.fromCompliant}/{m.fromTotal} → {m.toCompliant}/{m.toTotal}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={m.status} />
                <DeltaBadge delta={m.delta} status={m.status} />
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function SnapshotPicker({
  snapshots,
  fromId,
  toId,
  onChange,
}: {
  snapshots: { id: string; generatedAt: string; name: string | null }[];
  fromId: string;
  toId: string;
  onChange: (next: URLSearchParams) => void;
}) {
  const options = snapshots.map((s) => ({
    id: s.id,
    label: s.name ?? new Date(s.generatedAt).toLocaleString(),
  }));
  return (
    <section className="flex flex-wrap items-end gap-4 rounded-lg border border-border bg-surface p-7">
      <PickerSelect
        label="From"
        value={fromId}
        options={options}
        onChange={(v) => {
          const next = new URLSearchParams();
          next.set('from', v);
          next.set('to', toId);
          onChange(next);
        }}
      />
      <PickerSelect
        label="To"
        value={toId}
        options={options}
        onChange={(v) => {
          const next = new URLSearchParams();
          next.set('from', fromId);
          next.set('to', v);
          onChange(next);
        }}
      />
    </section>
  );
}

function PickerSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-label uppercase tracking-[0.08em] text-text-secondary">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 rounded-md border border-border bg-surface px-3 text-body text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Tile({
  label,
  fromValue,
  toValue,
  delta,
  unit,
  improved,
  neutral = false,
}: {
  label: string;
  fromValue: string;
  toValue: string;
  delta: number;
  unit: string;
  improved: boolean;
  neutral?: boolean;
}) {
  const tone = neutral
    ? 'text-text-secondary'
    : improved
      ? 'text-success'
      : 'text-danger';
  const sign = delta > 0 ? '+' : '';
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-7">
      <p className="text-label uppercase tracking-[0.08em] text-text-secondary">
        {label}
      </p>
      <div className="flex items-baseline gap-3">
        <span className="font-display text-h3 font-bold text-text-primary">
          {toValue}
        </span>
        <span className="text-body-sm text-text-secondary">from {fromValue}</span>
      </div>
      <p className={cn('text-body-sm font-medium', tone)}>
        {delta === 0 ? 'No change' : `${sign}${delta}${unit ? ` ${unit}` : ''}`}
      </p>
    </div>
  );
}

function StatusBadge({ status }: { status: ModuleDiffStatus }) {
  const config: Record<ModuleDiffStatus, { label: string; className: string }> = {
    improved: { label: 'Improved', className: 'bg-success/10 text-success' },
    regressed: { label: 'Regressed', className: 'bg-danger/10 text-danger' },
    unchanged: {
      label: 'Unchanged',
      className: 'bg-surface-tint text-text-secondary',
    },
    added: { label: 'New', className: 'bg-accent/10 text-accent' },
    removed: { label: 'Removed', className: 'bg-warning/10 text-warning' },
  };
  const c = config[status];
  return (
    <span
      className={cn(
        'rounded-full px-2.5 py-0.5 text-label font-medium uppercase tracking-[0.08em]',
        c.className,
      )}
    >
      {c.label}
    </span>
  );
}

function DeltaBadge({ delta, status }: { delta: number; status: ModuleDiffStatus }) {
  if (status === 'added' || status === 'removed') {
    return null;
  }
  const sign = delta > 0 ? '+' : '';
  return (
    <span
      className={cn(
        'min-w-[64px] text-right font-mono text-body-sm font-medium',
        delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-text-secondary',
      )}
    >
      {delta === 0 ? '—' : `${sign}${delta} pts`}
    </span>
  );
}

function Empty({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mx-auto max-w-content-wide px-5 py-gap-group md:px-10 lg:px-20">
      <h1 className="font-display text-h1 font-bold">{title}</h1>
      {description ? (
        <p className="mt-3 text-body text-text-secondary">{description}</p>
      ) : null}
      <Button asChild variant="dark" className="mt-6">
        <Link to="/report/snapshots">Go to snapshots</Link>
      </Button>
    </div>
  );
}
